import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import '../styles/Navbar.css';
import logo from '../images/logo.png';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Services', href: '/services' },
    { label: 'Features', href: '/features' },
    { label: 'Contact', href: '/contact' }
  ];

  const handleDemo = () => {
    navigate('/demo');
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    navigate('/signin', { replace: true });
  };

  return (
    <nav className={`navbar ${isDarkMode ? 'navbar--dark' : 'navbar--light'}`}>
      <div className="navbar__container">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <div className="navbar__logo-icon">
            <img src={logo} alt="Effective Global Staff Logo" />
          </div>
          <span>Effective Global Staff</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="navbar__nav-desktop">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`navbar__link ${isDarkMode ? 'navbar__link--dark' : 'navbar__link--light'}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right Section */}
        <div className="navbar__right">
          <ThemeToggle />

          {/* Desktop Buttons */}
          <div className="navbar__auth-desktop">
            <button
              onClick={handleDemo}
              className={`navbar__btn navbar__btn-signin ${isDarkMode ? 'navbar__btn-signin--dark' : 'navbar__btn-signin--light'}`}
            >
              Request Demo
            </button>

            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="btn btn-outline btn-sm">Dashboard</Link>
                <button onClick={handleLogout} className="btn btn-primary btn-sm">Logout</button>
              </>
            ) : (
              <>
                <Link to="/signin" className="btn btn-outline btn-sm">Sign In</Link>
                <Link to="/signup" className="btn btn-primary btn-sm">Sign Up</Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`navbar__menu-btn ${isDarkMode ? 'navbar__menu-btn--dark' : 'navbar__menu-btn--light'}`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          className={`navbar__mobile-menu ${isDarkMode ? 'navbar__mobile-menu--dark' : 'navbar__mobile-menu--light'}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`navbar__mobile-link ${isDarkMode ? 'navbar__mobile-link--dark' : 'navbar__mobile-link--light'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="navbar__mobile-auth" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button
              onClick={handleDemo}
              className={`navbar__btn navbar__btn-signin ${isDarkMode ? 'navbar__btn-signin--dark' : 'navbar__btn-signin--light'}`}
            >
              Request Demo
            </button>

            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="btn btn-outline" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                <button onClick={handleLogout} className="btn btn-primary">Logout</button>
              </>
            ) : (
              <>
                <Link to="/signin" className="btn btn-outline" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
                <Link to="/signup" className="btn btn-primary" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
              </>
            )}
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;